import {
    AudioDeviceInfo,
    Call,
    CallAgent,
    CallClient,
} from '@azure/communication-calling';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';

import { Log } from '@/shared/Log';
import { PhoneCallRejectedReason } from '@/shared/enums';
import { fetch } from '@/shared/fetch';
import { IStatus } from '@/shared/interfaces';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import { CallStartError } from '../model/CallStartError';
import { ConfirmationDto } from '../model/ConfirmationDto';

const heartbeatIntervalTiming = 3_000; // 3 seconds

let currentCall: PhoneCallHandler | null;
export type PhoneCallStatus = 'Ringing' | 'Connected' | 'Disconnected';
type PhoneCallStatusChangedListener = (status: PhoneCallStatus) => void;

export class PhoneCallHandler {
    private azureToken: string;
    private call: Call | null | undefined;
    private callAgent: CallAgent | undefined;
    private callClient = new CallClient();
    private callID: number | undefined;
    private consultationID: number;
    private onHangUp: () => void;
    private patientID: number;
    private patientPhoneNumber: string;
    private caregiverPhoneNumber: string;
    private heartbeatIntervalID:
        | ReturnType<typeof setInterval>
        | null
        | undefined;
    private phoneCallStatusChangedListeners: PhoneCallStatusChangedListener[] =
        [];
    private hangUpRequested = false;
    constructor(
        confirmationDto: ConfirmationDto,
        {
            onHangUp,
        }: {
            onHangUp: () => void;
        },
    ) {
        this.azureToken = confirmationDto.AzureToken;
        this.caregiverPhoneNumber = confirmationDto.CaregiverPhoneNumber;
        this.consultationID = confirmationDto.Consultation.ID;
        this.onHangUp = onHangUp;
        this.patientID = confirmationDto.Patient.User.ID;
        this.patientPhoneNumber = confirmationDto.PatientPhoneNumber;

        currentCall?.cleanUp();
        currentCall = this;
    }

    private async cleanUp(): Promise<void> {
        await this.callAgent?.dispose();
        this.callAgent = null;
    }

    private async ensureAgent() {
        if (this.callAgent == null) {
            const tokenCredential = new AzureCommunicationTokenCredential(
                this.azureToken,
            );
            this.callAgent =
                await this.callClient.createCallAgent(tokenCredential);
        }

        return this.callAgent;
    }

    async makeCall(enforceCall: boolean): Promise<CallStartError | null> {
        const { patientPhoneNumber, caregiverPhoneNumber } = this;
        if (patientPhoneNumber == null || caregiverPhoneNumber == null) {
            Log.error('One or more required phone numbers are missing', {
                patientPhoneNumber,
                caregiverPhoneNumber,
            });
            return CallStartError.unknown;
        }

        const registerResult = await this.registerCall(enforceCall);
        if (registerResult != null) {
            return registerResult;
        }

        if (this.hangUpRequested) {
            return null;
        }

        const callAgent = await this.ensureAgent();

        if (this.hangUpRequested) {
            return null;
        }

        const recipient = { phoneNumber: patientPhoneNumber };

        this.call = callAgent.startCall([recipient], {
            alternateCallerId: { phoneNumber: caregiverPhoneNumber },
        });

        this.call.on('stateChanged', () => {
            this.updateCall().catch((e) => this.handleError(e));

            if (this.call.state === 'Disconnected') {
                this.hangUp();
            }
        });

        if (this.hangUpRequested) {
            void this.hangUp();
        }

        return null;
    }

    /// Registers the call with the server. This returns true if the server agrees that the call should be made
    private async registerCall(
        enforceCall: boolean,
    ): Promise<CallStartError | null> {
        const status: IStatus<{ Id: number }> = await fetch(
            '/api/caregivers/phone-calls/register' +
                (enforceCall ? '?enforceCall=true' : ''),
            'post',
            `consultationId=${this.consultationID}`,
            'application/x-www-form-urlencoded',
        );

        if (status.Result != null) {
            this.callID = status.Result.Id;
            if (this.callID == null) {
                Log.error('Got no ID back when registering a phone call');
            }
            this.startHeartbeat();
            return null;
        } else {
            if (
                status.Errors.every(
                    (x) =>
                        x.Key ==
                        PhoneCallRejectedReason.ConsultationIsAlreadyTaken,
                )
            ) {
                return CallStartError.isHandledByOther;
            }

            Log.error('Failed to register phone call', status.Errors);

            return CallStartError.unknown;
        }
    }

    private async updateCall() {
        if (this.callID == null || this.call == null) return;

        let status: PhoneCallStatus;

        switch (this.call.state) {
            case 'Connecting':
            case 'Ringing':
                status = 'Ringing';
                break;
            case 'Connected':
            case 'InLobby':
                status = 'Connected';
                break;
            case 'Disconnecting':
            case 'Disconnected':
                status = 'Disconnected';
                break;
            case 'LocalHold':
            case 'RemoteHold':
            case 'EarlyMedia':
            case 'None':
                return;
        }

        await fetch(
            `/api/caregivers/phone-calls/${this.callID}/update`,
            'post',
            `status=${status}`,
            'application/x-www-form-urlencoded',
        );

        this.phoneCallStatusChangedListeners.forEach((x) => x(status));
    }

    private startHeartbeat() {
        if (this.heartbeatIntervalID != null) {
            return;
        }

        this.heartbeatIntervalID = setInterval(() => {
            this.sendHeartbeat().catch((e) => this.handleError(e));
        }, heartbeatIntervalTiming);
    }

    private stopHeartbeat() {
        if (this.heartbeatIntervalID != null) {
            clearInterval(this.heartbeatIntervalID);
            this.heartbeatIntervalID = null;
        }
    }

    private async sendHeartbeat() {
        if (this.callID == null) {
            return;
        }

        await fetch(
            `/api/caregivers/phone-calls/${this.callID}/heartbeat`,
            'post',
        );
    }

    public async viewClosed(): Promise<void> {
        if (this.call != null) {
            alert(R.PhoneCallWarnBeforeClosingView);
            await this.hangUp();
        }
        await this.cleanUp();
    }

    public async hangUp() {
        this.hangUpRequested = true;
        this.stopHeartbeat();
        if (this.call?.state !== 'Disconnected') {
            try {
                await this.call?.hangUp({ forEveryone: true });
            } catch (e) {
                this.handleError(e);
            }
        }
        this.call = null;
        this.onHangUp();
    }

    private handleError(error: Error) {
        Log.error(
            `Could not establish phone call to user ${this.patientID}`,
            null,
            error,
        );
    }

    public onStatusChanged(listener: PhoneCallStatusChangedListener) {
        this.phoneCallStatusChangedListeners.push(listener);
    }

    public offStatusChanged(listener: PhoneCallStatusChangedListener) {
        this.phoneCallStatusChangedListeners =
            this.phoneCallStatusChangedListeners.filter((x) => x !== listener);
    }

    public async setSelectedMicrophone(selectedMicrophone: AudioDeviceInfo) {
        const deviceManager = await this.callClient?.getDeviceManager();
        deviceManager?.selectMicrophone(selectedMicrophone);
    }
}
